import { parse } from 'node-html-parser'
import type { TocHeading, TocItem } from '@/types/toc-tree'

export const removeHtmlTags = (html: string) =>
	html
		.replace(/<\/?p[^>]*>/gi, ' ')
		.replace(/<\/?[^>]+(>|$)/g, '')
		.replace(/\s+/g, ' ')
		.replace(/&nbsp;/g, ' ')
		.trim()

export const generateIdSlug = (text: string): string =>
	text
		.toLowerCase()
		.trim()
		.replace(/[^\w\s-]/g, '')
		.replace(/\s+/g, '-')

export const getUniqueId = (base: string, usedIds: Set<string>) => {
	let id = base
	let counter = 1
	while (usedIds.has(id)) {
		id = `${base}-${counter++}`
	}
	return id
}

export const extractHeadingsFromHtml = (html: string): TocHeading[] => {
	const root = parse(html)
	const headings: TocHeading[] = []
	const usedIds = new Set<string>()

	const nodes = root.querySelectorAll('h1, h2, h3, h4, h5, h6')

	for (const node of nodes) {
		const tag = node.tagName.toLowerCase()
		const match = tag.match(/^h([1-6])$/)
		if (!match) continue
		const level = match[0] as TocHeading['level']
		const text = node.text.trim()
		if (!text) continue
		const baseId = generateIdSlug(text)
		const id = getUniqueId(baseId, usedIds)
		usedIds.add(id)
		headings.push({ text, level, id })
	}

	return headings
}

export const buildTocTree = (flatHeadings: TocHeading[]): TocItem[] => {
	const root: TocItem[] = []
	const stack: TocItem[] = []

	for (const heading of flatHeadings) {
		if (!heading.text || !heading.id) continue // Skip empty headings

		const currentItem: TocItem = { ...heading, children: [] }
		const currentLevel = Number.parseInt(heading?.level?.[1] ?? '', 10)

		while (
			stack.length &&
			Number.parseInt(stack[stack.length - 1]?.level[1] ?? '', 10) >= currentLevel
		) {
			stack.pop()
		}

		if (stack.length === 0) {
			root.push(currentItem)
		} else {
			stack[(stack?.length ?? 0) - 1]?.children.push(currentItem)
		}

		stack.push(currentItem)
	}

	return root
}

export const isHtmlString = (value: string) => {
	return /<\/?[a-z][\s\S]*>/i.test(value)
}
